<template>
  <portal to="global-modal-portal" :disabled="!usePortal">
    <b-modal :active.sync="isModalShowing" :width="width" scroll="keep" :has-modal-card="true">
      <div class="modal-card related-party-modal" style="width: auto">
        <header class="modal-card-head rp-head">
          <div class="rp-head-text">
            <p class="modal-card-title rp-title">{{ title }}</p>
            <p v-if="subtitle" class="rp-subtitle">{{ subtitle }}</p>
          </div>
        </header>
        <section class="modal-card-body rp-body-section">
          <div class="related-party-list">
            <button v-for="assoc in partyOptions"
               :key="assoc.id"
               type="button"
               class="related-party-row"
               :class="{ 'related-party-row--selected': assoc === (selectedParty || originalParty) }"
               :aria-pressed="assoc === (selectedParty || originalParty) ? 'true' : 'false'"
               @click="onSelectParty(assoc)">
              <span class="rp-avatar" :class="{ 'rp-avatar--company': isCompany(assoc) }">
                <i v-if="isCompany(assoc)" class="fas fa-building"></i>
                <span v-else>{{ initials(assoc.targetCustomerName) }}</span>
              </span>
              <span class="rp-info">
                <span class="rp-name">{{ assoc.targetCustomerName }}</span>
                <span v-if="assoc.targetCustomerEmail || assoc.targetCustomerMobile" class="rp-contact">
                  <span v-if="assoc.targetCustomerEmail">{{ assoc.targetCustomerEmail }}</span>
                  <span v-if="assoc.targetCustomerEmail && assoc.targetCustomerMobile" class="rp-dot">·</span>
                  <span v-if="assoc.targetCustomerMobile">{{ assoc.targetCustomerMobile }}</span>
                </span>
              </span>
              <span class="rp-relation" :class="{ 'rp-relation--company': isCompany(assoc) }">{{ assoc.associationTypeDescription }}</span>
              <span class="rp-selection" aria-hidden="true">{{ assoc === (selectedParty || originalParty) ? '\u2713' : '' }}</span>
            </button>
          </div>
        </section>
        <footer class="modal-card-foot rp-foot">
          <button type="button" class="button is-success rp-confirm" @click="onConfirm">{{ selectedParty ? confirmLabel : noneLabel }}</button>
        </footer>
      </div>
    </b-modal>
  </portal>
</template>

<script>
export default {
  name: 'RelatedPartySelectionModal',
  props: {
    relatedParties: {
      type: Array,
      default: () => []
    },
    originalCustomer: {
      type: Object,
      default: null
    },
    title: {
      type: String,
      default: 'Select a customer'
    },
    subtitle: {
      type: String,
      default: ''
    },
    noneLabel: {
      type: String,
      default: 'Ok'
    },
    confirmLabel: {
      type: String,
      default: 'Ok'
    },
    usePortal: {
      type: Boolean,
      default: true
    },
    width: {
      type: Number,
      default: 560
    }
  },
  data () {
    return {
      isModalShowing: true,
      closedBySelection: false,
      selectedParty: null
    }
  },
  computed: {
    originalParty () {
      if (!this.originalCustomer) return null
      return {
        id: 'original',
        targetCustomerName: this.originalCustomer.fullName,
        targetCustomerEmail: this.originalCustomer.email,
        targetCustomerMobile: this.originalCustomer.mobile,
        targetCustomerType: this.originalCustomer.customerType,
        associationTypeDescription: 'Original customer'
      }
    },
    partyOptions () {
      return this.originalParty ? [this.originalParty, ...this.relatedParties] : this.relatedParties
    }
  },
  methods: {
    isCompany: function (assoc) {
      return assoc.targetCustomerType === 'Company'
    },
    initials: function (name) {
      if (!name) {
        return '?'
      }
      var parts = name.trim().split(/\s+/)
      if (parts.length === 1) {
        return parts[0].charAt(0).toUpperCase()
      }
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
    },
    onSelectParty: function (assoc) {
      this.selectedParty = assoc === this.originalParty || (!this.originalCustomer && this.selectedParty === assoc) ? null : assoc
    },
    onConfirm: function () {
      if (!this.isModalShowing) return
      if (this.selectedParty) {
        this.closedBySelection = true
        this.$emit('on-select', this.selectedParty)
      }
      this.isModalShowing = false
    },
    // Keeping the original, skipping, and dismissing all resolve to on-none.
    onNone: function () {
      this.isModalShowing = false
    }
  },
  watch: {
    isModalShowing: function (newValue) {
      if (!newValue && !this.closedBySelection) {
        this.$emit('on-none')
      }
    }
  },
  mounted () {
    this.isModalShowing = true
  }
}
</script>

<style scoped>
  .related-party-modal {
    border-radius: 12px;
    overflow: hidden;
  }
  .rp-head {
    background: #fbfcfd;
    border-bottom: 1px solid #eceff3;
    padding: 16px 20px;
  }
  .rp-title {
    font-size: 1.05rem;
    font-weight: 700;
    color: #1f2430;
  }
  .rp-subtitle {
    margin-top: 3px;
    font-size: 0.8rem;
    font-weight: 400;
    color: #8a94a6;
  }
  .rp-body-section {
    padding: 16px 20px 20px;
  }
  .related-party-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
    max-height: 60vh;
    overflow-y: auto;
    padding: 2px;
  }
  .related-party-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    border: 1px solid #e6e9ef;
    border-radius: 10px;
    background: #fff;
    color: inherit;
    font: inherit;
    text-align: left;
    cursor: pointer;
    transition: box-shadow 140ms ease, border-color 140ms ease, transform 140ms ease, background-color 140ms ease;
  }
  .related-party-row:hover {
    border-color: #b9c6d6;
    box-shadow: 0 4px 14px rgba(20, 30, 50, 0.08);
    transform: translateY(-1px);
  }
  .related-party-row:active {
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(20, 30, 50, 0.08);
  }
  .related-party-row--selected {
    background: #eaf6ee;
    border-color: #88b99b;
  }
  .related-party-row:focus-visible {
    outline: 2px solid #2f7d54;
    outline-offset: 2px;
  }
  .rp-avatar {
    flex: 0 0 auto;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 0.85rem;
    letter-spacing: 0.02em;
    background: #e9f4ee;
    color: #2f7d54;
  }
  .rp-avatar--company {
    background: #eaf1fb;
    color: #2b6cb0;
    font-size: 1rem;
  }
  .rp-info {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }
  .rp-name {
    font-weight: 600;
    color: #1f2430;
    line-height: 1.2;
  }
  .rp-contact {
    margin-top: 2px;
    font-size: 0.78rem;
    color: #8a94a6;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .rp-dot {
    margin: 0 5px;
    color: #c2cad6;
  }
  .rp-relation {
    flex: 0 0 auto;
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    padding: 3px 10px;
    border-radius: 999px;
    background: #eaf6ee;
    color: #2f7d54;
    border: 1px solid #cfe9d8;
    white-space: nowrap;
  }
  .rp-relation--company {
    background: #eaf1fb;
    color: #2b6cb0;
    border-color: #cfe0f5;
  }
  .rp-selection {
    flex: 0 0 auto;
    width: 16px;
    color: #2f7d54;
    font-weight: 700;
  }
  .rp-foot {
    justify-content: flex-end;
    padding: 12px 20px;
    background: #fbfcfd;
    border-top: 1px solid #eceff3;
  }
</style>
